import React, { useState, useEffect } from 'react';
import userService from '../../services/userService.js';
import './UserManagement.css';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [editingUser, setEditingUser] = useState(null);
    const [editingUserId, setEditingUserId] = useState(null); // Track the original user ID being edited
    const [newPassword, setNewPassword] = useState('');
    const [newUser, setNewUser] = useState({
        id: undefined, // Will be auto-assigned if not specified
        username: '',
        password_hash: '$2b$10$placeholder_hash', // Placeholder for now
        first_name: '',
        last_name: '',
        display_name: '',
        email: '',
        phone: '',
        role: 'family_member',
        can_do_weekend_duty: true,
        color: '#4FB06D',
        family_group: 'main'
    });

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const allUsers = await userService.getAllUsers();
            setUsers(allUsers);
            setError('');
        } catch (err) {
            setError(`Error loading users: ${err.message}`);
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);

            // Prepare user data for creation
            const userData = { ...newUser };

            // Remove id if it's undefined (let database auto-assign)
            if (!userData.id) {
                delete userData.id;
            }

            await userService.createUser(userData);
            setNewUser({
                id: undefined,
                username: '',
                password_hash: '$2b$10$placeholder_hash',
                first_name: '',
                last_name: '',
                display_name: '',
                email: '',
                phone: '',
                role: 'family_member',
                can_do_weekend_duty: true,
                color: '#4FB06D',
                family_group: 'main'
            });
            await fetchUsers();
            setError('');
        } catch (err) {
            setError(`Error creating user: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        let updateData;

        try {
            setLoading(true);

            // Prepare update data
            updateData = { ...editingUser };

            // Only include password if a new one was entered
            if (newPassword.trim()) {
                updateData.password_hash = newPassword;
            } else {
                // Remove password_hash from update data if no new password
                delete updateData.password_hash;
            }

            // Check if ID is being changed
            if (updateData.id !== editingUser.id) {
                const confirmIdChange = window.confirm(
                    `⚠️ WARNING: You are changing the user ID from ${editingUser.id} to ${updateData.id}.\n\n` +
                    `This will:\n` +
                    `• Create a new user record with ID ${updateData.id}\n` +
                    `• Delete the old user record with ID ${editingUser.id}\n` +
                    `• Note: Calendar entries will NOT be automatically updated\n\n` +
                    `Are you sure you want to continue?`
                );

                if (!confirmIdChange) {
                    setLoading(false);
                    return;
                }
            }

            await userService.updateUser(editingUser.id, updateData);
            setEditingUser(null);
            setEditingUserId(null);
            setNewPassword('');
            await fetchUsers();
            setError('');
        } catch (err) {
            console.error('User update error details:', {
                error: err,
                message: err.message,
                editingUser: editingUser
            });
            setError(`Error updating user: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = async (userId) => {
        if (window.confirm('Are you sure you want to deactivate this user?')) {
            try {
                setLoading(true);
                await userService.deleteUser(userId);
                await fetchUsers();
                setError('');
            } catch (err) {
                setError(`Error deactivating user: ${err.message}`);
            } finally {
                setLoading(false);
            }
        }
    };

    const startEdit = (user) => {
        setEditingUser({ ...user });
        setEditingUserId(user.id); // Store the original user ID
        setNewPassword(''); // Clear password field when starting to edit
    };

    const cancelEdit = () => {
        setEditingUser(null);
        setEditingUserId(null);
        setNewPassword('');
    };

    if (loading && users.length === 0) {
        return (
            <div className="user-management">
                <div className="loading-spinner">Loading users...</div>
            </div>
        );
    }

    return (
        <div className="user-management">
            <div className="user-management-header">
                <h2>👥 User Management</h2>
                <p>Manage family members and user accounts</p>
            </div>

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            <div className="user-management-sections">
                {/* Create New User */}
                <div className="create-user-section">
                    <h3>➕ Create New User</h3>
                    <form onSubmit={handleCreateUser} className="user-form">
                        <div className="form-row">
                            <div className="form-group">
                                <label>ID (optional - leave blank for auto-assignment):</label>
                                <input
                                    type="number"
                                    value={newUser.id || ''}
                                    onChange={(e) => setNewUser({ ...newUser, id: e.target.value ? parseInt(e.target.value) : undefined })}
                                    min="1"
                                    placeholder="Auto-assign if blank"
                                />
                            </div>
                            <div className="form-group">
                                <label>Username:</label>
                                <input
                                    type="text"
                                    value={newUser.username}
                                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                                    required
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Display Name:</label>
                                <input
                                    type="text"
                                    value={newUser.display_name}
                                    onChange={(e) => setNewUser({ ...newUser, display_name: e.target.value })}
                                    required
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>First Name:</label>
                                <input
                                    type="text"
                                    value={newUser.first_name}
                                    onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Last Name:</label>
                                <input
                                    type="text"
                                    value={newUser.last_name}
                                    onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                                    required
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Email:</label>
                                <input
                                    type="email"
                                    value={newUser.email}
                                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Phone:</label>
                                <input
                                    type="text"
                                    value={newUser.phone}
                                    onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Color:</label>
                                <input
                                    type="color"
                                    value={newUser.color}
                                    onChange={(e) => setNewUser({ ...newUser, color: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Weekend Duty:</label>
                                <select
                                    value={newUser.can_do_weekend_duty}
                                    onChange={(e) => setNewUser({ ...newUser, can_do_weekend_duty: e.target.value === 'true' })}
                                >
                                    <option value={true}>Yes</option>
                                    <option value={false}>No</option>
                                </select>
                            </div>
                        </div>
                        <button type="submit" className="create-btn" disabled={loading}>
                            {loading ? 'Creating...' : 'Create User'}
                        </button>
                    </form>
                </div>

                {/* User List */}
                <div className="users-list-section">
                    <h3>📋 Current Users</h3>
                    <div className="users-grid">
                        {users.map(user => (
                            <div key={user.id} className={`user-card ${editingUserId === user.id ? 'editing' : ''}`}>
                                <div className="user-header">
                                    <div
                                        className="user-color"
                                        style={{ backgroundColor: user.color }}
                                    ></div>
                                    <div className="user-info">
                                        <h4>{user.display_name}</h4>
                                        <p className="user-details">
                                            {user.first_name} {user.last_name}
                                        </p>
                                        <p className="user-username">@{user.username}</p>
                                    </div>
                                    <div className="user-status">
                                        <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                                            {user.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                        {user.can_do_weekend_duty && (
                                            <span className="duty-badge">Weekend Duty</span>
                                        )}
                                    </div>
                                </div>

                                <div className="user-details-row">
                                    <div className="detail-item">
                                        <span className="detail-label">Role:</span>
                                        <span className="user-role">{user.role}</span>
                                    </div>
                                    {user.email && (
                                        <div className="detail-item">
                                            <span className="detail-label">Email:</span>
                                            <span className="user-email">{user.email}</span>
                                        </div>
                                    )}
                                    {user.phone && (
                                        <div className="detail-item">
                                            <span className="detail-label">Phone:</span>
                                            <span className="user-phone">{user.phone}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="user-actions">
                                    <button
                                        onClick={() => startEdit(user)}
                                        className="edit-btn"
                                    >
                                        ✏️ Edit
                                    </button>
                                    <button
                                        onClick={() => handleDeleteUser(user.id)}
                                        className="delete-btn"
                                    >
                                        🗑️ Deactivate
                                    </button>
                                </div>

                                {/* Edit Form */}
                                {editingUser && editingUserId === user.id && (
                                    <div className="edit-form">
                                        <h4>Edit User: {editingUser.display_name}</h4>
                                        <form onSubmit={handleUpdateUser}>
                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label>ID:</label>
                                                    <input
                                                        type="number"
                                                        value={editingUser.id}
                                                        onChange={(e) => setEditingUser({ ...editingUser, id: parseInt(e.target.value) })}
                                                        min="1"
                                                        required
                                                    />
                                                    <small className="id-note">
                                                        Warning: Changing ID will NOT automatically update calendar entries. The edit form will stay with the original user until saved.
                                                    </small>
                                                </div>
                                                <div className="form-group">
                                                    <label>Username:</label>
                                                    <input
                                                        type="text"
                                                        value={editingUser.username}
                                                        onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label>Display Name:</label>
                                                    <input
                                                        type="text"
                                                        value={editingUser.display_name}
                                                        onChange={(e) => setEditingUser({ ...editingUser, display_name: e.target.value })}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label>First Name:</label>
                                                    <input
                                                        type="text"
                                                        value={editingUser.first_name}
                                                        onChange={(e) => setEditingUser({ ...editingUser, first_name: e.target.value })}
                                                        required
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label>Last Name:</label>
                                                    <input
                                                        type="text"
                                                        value={editingUser.last_name}
                                                        onChange={(e) => setEditingUser({ ...editingUser, last_name: e.target.value })}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label>Email:</label>
                                                    <input
                                                        type="email"
                                                        value={editingUser.email || ''}
                                                        onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label>Phone:</label>
                                                    <input
                                                        type="text"
                                                        value={editingUser.phone || ''}
                                                        onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label>Color:</label>
                                                    <input
                                                        type="color"
                                                        value={editingUser.color}
                                                        onChange={(e) => setEditingUser({ ...editingUser, color: e.target.value })}
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label>Role:</label>
                                                    <select
                                                        value={editingUser.role}
                                                        onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                                                    >
                                                        <option value="family_member">Family Member</option>
                                                        <option value="admin">Admin</option>
                                                        <option value="caregiver">Caregiver</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label>Weekend Duty:</label>
                                                    <select
                                                        value={editingUser.can_do_weekend_duty}
                                                        onChange={(e) => setEditingUser({ ...editingUser, can_do_weekend_duty: e.target.value === 'true' })}
                                                    >
                                                        <option value={true}>Yes</option>
                                                        <option value={false}>No</option>
                                                    </select>
                                                </div>
                                                <div className="form-group">
                                                    <label>Active:</label>
                                                    <select
                                                        value={editingUser.is_active}
                                                        onChange={(e) => setEditingUser({ ...editingUser, is_active: e.target.value === 'true' })}
                                                    >
                                                        <option value={true}>Yes</option>
                                                        <option value={false}>No</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label>Family Group:</label>
                                                    <input
                                                        type="text"
                                                        value={editingUser.family_group || ''}
                                                        onChange={(e) => setEditingUser({ ...editingUser, family_group: e.target.value })}
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label>Password (leave blank to keep current):</label>
                                                    <input
                                                        type="password"
                                                        value={newPassword}
                                                        placeholder="Enter new password or leave blank"
                                                        onChange={(e) => setNewPassword(e.target.value)}
                                                    />
                                                    <small className="password-note">
                                                        Note: Passwords are stored as plain text for now. In production, use proper hashing.
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="form-actions">
                                                <button type="submit" className="save-btn" disabled={loading}>
                                                    {loading ? 'Saving...' : 'Save Changes'}
                                                </button>
                                                <button type="button" onClick={cancelEdit} className="cancel-btn">
                                                    Cancel
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                </div>
            </div>
        </div>
    );
};

export default UserManagement;
